import type { Answers, QuestionDef } from "../types/questions";
import type { QuestionGraph } from "./types";

export function isTerminalNext(next: string, questionGraph: QuestionGraph): boolean {
  return questionGraph[next] === undefined;
}

export function getQuestionById(questionGraph: QuestionGraph, id: string): QuestionDef | undefined {
  return questionGraph[id];
}

export function getNextTargetForAnswer(
  questionGraph: QuestionGraph,
  questionId: string,
  selectedValue: string,
): string | null {
  const question = questionGraph[questionId];
  if (!question) {
    return null;
  }

  const selectedOption = question.options.find((option) => option.value === selectedValue);
  return selectedOption?.next ?? null;
}

function getMaxQuestionDepthFrom(questionGraph: QuestionGraph, id: string): number {
  const node = questionGraph[id];
  if (!node) {
    return 0;
  }

  let maxChildDepth = 0;

  for (const option of node.options) {
    if (isTerminalNext(option.next, questionGraph)) {
      continue;
    }

    maxChildDepth = Math.max(maxChildDepth, getMaxQuestionDepthFrom(questionGraph, option.next));
  }

  return 1 + maxChildDepth;
}

function getDepthFollowingAnswers(
  questionGraph: QuestionGraph,
  id: string,
  answers: Answers,
): number {
  const node = questionGraph[id];
  if (!node) {
    return 0;
  }

  const selectedValue = answers[id];
  if (!selectedValue) {
    return getMaxQuestionDepthFrom(questionGraph, id);
  }

  const selectedOption = node.options.find((option) => option.value === selectedValue);
  if (!selectedOption || isTerminalNext(selectedOption.next, questionGraph)) {
    return 1;
  }

  return 1 + getDepthFollowingAnswers(questionGraph, selectedOption.next, answers);
}

export function getQuestionStepsForPath(
  questionGraph: QuestionGraph,
  currentQuestionId: string,
  answers: Answers,
): number {
  return getDepthFollowingAnswers(questionGraph, currentQuestionId, answers);
}

export function resolveTerminalFromAnswers(
  questionGraph: QuestionGraph,
  answers: Answers,
  startQuestionId: string,
): string | null {
  const visited = new Set<string>();
  let currentQuestionId: string = startQuestionId;

  while (true) {
    if (visited.has(currentQuestionId)) {
      throw new Error(`Cycle detected in question tree at "${currentQuestionId}"`);
    }
    visited.add(currentQuestionId);

    const selectedValue = answers[currentQuestionId];
    if (!selectedValue) {
      return null;
    }

    const nextTarget = getNextTargetForAnswer(questionGraph, currentQuestionId, selectedValue);
    if (!nextTarget) {
      return null;
    }

    if (isTerminalNext(nextTarget, questionGraph)) {
      return nextTarget;
    }

    currentQuestionId = nextTarget;
  }
}
